import { fork, put, race, select, take, takeEvery } from "redux-saga/effects";
import {
  closeConfirmModal,
  CloseConfirmModalAction,
  closeDispatchingModal,
  openConfirmModal,
  PaymentAction,
  publishModalResult,
} from "actions";
import { v4 as uuidv4 } from "uuid";

import { ModalTypes } from "constants/navigation";
import {
  DELETE_PAYMENT_FROM_MODAL,
  PAYMENT,
  SUBMIT_PAYMENT_FORM,
} from "constants/actionTypes";
import {
  DeleteConfirmedAction,
  deletePaymentFromModalConfirmed,
  PromptDeleteAction,
  SubmitFormAction,
} from "actions/modals/payment";
import {
  getActiveRole,
  getPaymentById,
  getSaleLots,
  getSetting,
} from "selectors";
import { closeModal, getClearingSaleBuyerReceiptUrl } from "lib/navigation";
import { Settings } from "constants/settings";
import toast from "lib/toast";
import { createModalTitle } from "components/ConfirmDialog";
import { ClearingSaleLotStatus } from "constants/clearingSaleAttributes";

function* openReceiptOnResult(
  businessId: string,
  roleSlug: string,
  actionTypes: Record<string, string>,
) {
  const { success, failure } = yield race({
    success: take(actionTypes.SUCCESS),
    failure: take(actionTypes.FAILURE),
  });

  if (failure) {
    toast.error("Not opening receipt: Payment wasn't updated.");
  } else if (success) {
    // Open the receipt in a new tab
    window.open(getClearingSaleBuyerReceiptUrl(businessId, roleSlug), "_blank");
  }
}

function* onSubmitForm(action: SubmitFormAction) {
  const { values, isNew } = action;

  const state: State = yield select();

  const roleSlug: string = getActiveRole(state).slug;

  if (isNew) {
    const tempId = uuidv4();
    const paymentValues = {
      ...values,
      id: tempId,
    };

    toast.syncing("Creating payment...");
    yield put(PaymentAction.create(paymentValues));

    if (getSetting(Settings.paymentModalAddAction)(state) === "Add & Receipt") {
      yield fork(
        openReceiptOnResult,
        // For create the "from business id" will always be in the payload
        values.fromBusinessId,
        roleSlug,
        PAYMENT.CREATE,
      );
    }

    yield put(publishModalResult(ModalTypes.EditPayment, tempId));
  } else {
    toast.syncing("Updating payment...");
    yield put(PaymentAction.update(values));
    if (
      getSetting(Settings.paymentModalUpdateAction)(state) ===
      "Update & Receipt"
    ) {
      const payment = getPaymentById(values.id)(state);
      // for edit, the from business id may not be changing, so fall back to existing payment value
      const businessId = values.fromBusinessId ?? payment.fromBusinessId;
      yield fork(openReceiptOnResult, businessId, roleSlug, PAYMENT.UPDATE);
    }
  }

  yield put(closeDispatchingModal(ModalTypes.EditPayment));
}

function getConfirmDeleteDeleteActionFactory(
  paymentId,
  resetLotsToSold = false,
): () => DeleteConfirmedAction {
  return () => deletePaymentFromModalConfirmed(paymentId, resetLotsToSold);
}

function getCancelDeleteAction(): CloseConfirmModalAction {
  return closeConfirmModal();
}

function* onDeletePayment(action: PromptDeleteAction) {
  const { paymentId } = action;
  const state: State = yield select();

  const payment: Payment | null = getPaymentById(paymentId)(state);

  if (!payment) {
    toast.error("Could not delete payment: Payment not found.");
    return;
  }

  // Get all sale lots and find those that are PAID and belong to this payment's buyer
  const allSaleLots = getSaleLots(state);
  const paidLotsForBuyer = Object.values(allSaleLots).filter(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Sale lot object from state
    (lot: any) =>
      lot.buyer_id === payment.fromBusinessId &&
      lot.clearingSaleAttributes?.status === ClearingSaleLotStatus.PAID,
  );

  const hasPaidLots = paidLotsForBuyer.length > 0;

  // Future enhancement: Check if the payment is linked to any invoices or other records that would prevent deletion

  if (hasPaidLots) {
    // Show confirmation modal with option to reset lots to Sold
    yield put(
      openConfirmModal({
        title: createModalTitle("this Payment"),
        message:
          "You're deleting a payment relating to Lots marked Paid. Would you like to reset them to Sold status?",
        actions: [
          {
            dataTour: "cancelDeletePayment",
            label: "Cancel",
            onClickAction: getCancelDeleteAction,
            secondary: true,
          },
          {
            dataTour: "keepAsIsDeletePayment",
            label: "Delete and keep as is",
            onClickAction: getConfirmDeleteDeleteActionFactory(paymentId),
          },
          {
            dataTour: "resetToSoldDeletePayment",
            label: "Delete and reset to Sold",
            onClickAction: getConfirmDeleteDeleteActionFactory(paymentId, true),
          },
        ],
      }),
    );
  } else {
    // Show standard confirmation modal

    yield put(
      openConfirmModal({
        title: createModalTitle("this Payment"),
        message:
          "Are you sure you want to delete this payment? This action cannot be undone.",
        actions: [
          {
            dataTour: "cancelDeletePayment",
            label: "Cancel",
            onClickAction: getCancelDeleteAction,
            secondary: true,
          },
          {
            dataTour: "confirmDeletePayment",
            label: "Delete Payment",
            onClickAction: getConfirmDeleteDeleteActionFactory(paymentId),
          },
        ],
      }),
    );
  }
}

function* onConfirmDeletePayment(action: DeleteConfirmedAction) {
  const { paymentId, resetToSold } = action;
  const state: State = yield select();

  const payment: Payment | null = getPaymentById(paymentId)(state);

  if (!payment) {
    toast.error("Could not delete payment: Payment not found.");
    return;
  }

  closeModal(); // Close the Payment Modal
  yield put(closeConfirmModal()); // Close the Confirm Deletion Modal

  // Pass resetToSold parameter to the delete action options
  yield put(
    PaymentAction.delete(paymentId, {
      suppressToast: false,
      suppressErrorToast: false,
      suppressSuccessToast: false,
      resetToSold,
    }),
  );
}

export function* businessPaymentModalSagas() {
  yield takeEvery(SUBMIT_PAYMENT_FORM, onSubmitForm);
  yield takeEvery(DELETE_PAYMENT_FROM_MODAL.ACTION, onDeletePayment);
  yield takeEvery(DELETE_PAYMENT_FROM_MODAL.REQUEST, onConfirmDeletePayment);
}
